import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';

import H2 from '../H2';
import Stacheldraht from '../Stacheldraht';

import { mediaPhone, minTablet, minDesktop } from '../styles/breakpoints';
import { PTBold } from '../styles/fonts';
import { PrimaryColor, PrimaryDarkColor } from '../styles/colors';

import ShowMoreIconImg from './icons/outline-add_circle_outline-24px.svg';
import KatharinaImg from './images/Katharina_Hochmuth-BfA_600.jpg';
// import UnterschriftImg from './images/unterschrift.png';

const P = styled.p`
  font-size: 16px;
  line-height: 1.4;

  ${minDesktop} {
    font-size: 22px;
  }

  margin-bottom: 0;

  & + &::first-letter {
    margin-left: 1rem;
  }
`;

const StacheldrahtStyled = styled(Stacheldraht)`
  margin-top: -30px;

  ${minDesktop} {
    display: none;
  }
`;

const Article = styled.article`
  @media (min-width: 540px) {
    margin-top: 35px;
  }
  ${minTablet} {
    margin-bottom: 30px;
  }
  @media (min-width: 950px) {
    margin-top: 35px;
  }
  ${minDesktop} {
    margin-top: 55px;
  }
`;

const ExtendedSection = styled.section`
  display: ${({ show }) => (show ? 'block' : 'none')};
  margin-top: 10px;
`;

const ResetButtonStyles = css`
  -webkit-appearance: none;
  border: none;
  background-color: transparent;

  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: none;
  }
`;

const ShowMoreButton = styled.button([
  ResetButtonStyles,
  PTBold,
  `
  font-size: 16px;
  ${mediaPhone} {
    font-size: 14px;
  }

  color: ${PrimaryColor};

  &:hover {
    color: ${PrimaryDarkColor};
  }

  display: block;
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 36px;
  line-height: 24px;

  background-size: 24px 24px;
  background-repeat: no-repeat;
  background-position: left;
  background-image: url(${ShowMoreIconImg});
`]);

const IntroFooter = styled.footer`
  position: relative;
  padding-top: 30px;
  min-height: 250px;

  @media (max-width: 600px) {
    padding-top: 20px;
  }
`;

// const Unterschrift = styled.div`
//   & > img {
//     width: 222px;
//   }
//   @media (max-width: 600px) {
//     margin-bottom: 15px;
//   }
// `;

const Katharina = styled.div`
  position: absolute;
  top: 40px;
  right: 0;

  & > img {
    width: 300px;
  }

  @media (max-width: 600px) {
    position: static;
    margin-left: 0;
  }
`;

const Absender = styled.div`
  padding-top: 20px;
  padding-right: 320px;
  @media (max-width: 600px) {
    padding-right: 0;
  }
  max-width: 820px;
`;

const AbsenderName = styled(P)`
  padding-bottom: 20px;
`;

const H2Styled = styled(H2)`
  margin-top: 20px;
`;

class Introduction extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showLongContent: false,
    };
  }

  showLongContent() {
    this.setState({ showLongContent: true });
  }

  render() {
    const { className } = this.props;
    const { showLongContent } = this.state;

    return (
      <Article className={className}>
        <section>
          <P>
            Zur Gestaltung Ihres Unterrichts finden Sie hier das Unterrichtsprojekt
            „Familien und Flucht aus der DDR“ zum Download, Bildmaterialien zur Verwendung
            auf dem Smartboard, Quellennachweise und weiterführende Materialien zu den
            Themenbereichen DDR-Geschichte, Grundrechte sowie Flucht damals und heute.
          </P>
          <H2Styled>
            Grußwort
          </H2Styled>
          <P>
            1989 stürzten die Bürgerinnen und Bürger der DDR in einer Friedlichen Revolution die
            SED-Herrschaft. Sie eroberten sich damit nicht nur Freiheit, demokratische Souveränität,
            Menschenrechte, einen Rechtsstaat und schließlich auch die deutsche Einheit. Die
            Menschen in der DDR brachten zugleich die vielen sichtbaren und unsichtbaren Mauern zu
            Fall, die ihr Leben in der SED-Diktatur über Jahrzehnte hinweg bestimmt hatten.
          </P>
          { showLongContent || (
            <ShowMoreButton onClick={() => this.showLongContent()}>Mehr anzeigen</ShowMoreButton>
          )}
        </section>
        <ExtendedSection show={showLongContent}>
          <P>
            In Deutschland wachsen heute die Kinder ganz selbstverständlich in einer Gesellschaft mit
            vielen Freiheiten und stabilen demokratischen Institutionen auf. Zugleich wissen sie oft nur
            wenig über die DDR und den Alltag hinter der Mauer. Das Leben ihrer Eltern und Großeltern
            im geteilten Deutschland ist für sie heute kaum noch vorstellbar. Das Eingesperrtsein der
            DDR-Bürger hinter der Mauer, die staatliche Bevormundung in fast allen Bereichen des
            Lebens und die damit verbundenen Demütigungen, der vielgehegte und manchmal auch
            verwirklichte Wunsch, aus dieser Enge in die Freiheit zu fliehen – über all dies können sie
            heute nur noch etwas aus „zweiter Hand“ erfahren. Doch sie wollen und sollen mehr wissen
            über das Leben zwischen Alltag und Repression, Anpassung, Arrangement und
            Verweigerung. Denn für unsere heutige demokratische Gesellschaft ist es ungemein wichtig,
            dass diese Vergangenheit nicht vergessen wird und schon die ganz jungen Schülerinnen und
            Schüler den elementaren Unterschied zwischen Diktatur und Demokratie verstehen lernen.
            Auch um zu erklären, warum so viele Menschen der DDR während ihres 40-jährigen
            Bestehens unter schwierigsten Bedingungen und teilweise unter Lebensgefahr den Rücken
            kehrten. Dies gilt für den Osten genauso wie für den Westen. Hierbei helfen
            Bildungsmaterialien mit einer gegenwartsbezogenen Ausrichtung, die auch die
            Familiengeschichten der Schülerinnen und Schüler mit einbinden und den jungen Menschen
            Brücken aus der Vergangenheit in ihre Lebenswirklichkeit bauen. So kann
            Geschichtsaufarbeitung heute gut gelingen!
          </P>
          <IntroFooter>
            <Katharina>
              <img src={KatharinaImg} alt="Ein Bild von Katharina Hochmuth" />
            </Katharina>
            <Absender>
              <AbsenderName>
                Katharina Hochmuth
              </AbsenderName>
              <P>
                Leiterin des Arbeitsbereichs Schulische Bildungsarbeit,
                Bundesstiftung zur Aufarbeitung der SED-Diktatur
              </P>
            </Absender>
          </IntroFooter>
        </ExtendedSection>
        <StacheldrahtStyled small />
      </Article>
    );
  }
}

Introduction.propTypes = {
  className: PropTypes.string,
};

Introduction.defaultProps = {
  className: undefined,
};

export default Introduction;



// WEBPACK FOOTER //
// ./src/components/AboutMaterialDownload/Introduction.js